import * as React from 'react';
import { useMemo, useState, useCallback, useRef } from 'react';
import { Box, Typography, Snackbar, Alert } from '@mui/material';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import { can } from '../../utils/access';
import { useUserStore } from '../../store';
import useCalendarState from './hooks/useCalendarState';
import { isToday, parseISO, format, setHour, parseWorkScheduleHours } from './hooks/dateUtils';
import {
  HOUR_HEIGHT,
  TIME_COLUMN_WIDTH,
  RESOURCE_COLUMN_WIDTH,
  UI_COLORS,
  BOOKING_STATUS_CONFIG,
  BREAK_PERIOD_CONFIG,
  SNAPS_PER_HOUR,
  calculateEventOverlaps,
} from './calendarConstants';
import { applyEventFilters } from './hooks/eventFilters';
import MaintenanceEvent from './MaintenanceEvent';
import UnavailabilityEvent from './UnavailabilityEvent';
import CalendarNoteEvent from './CalendarNoteEvent';
import DragSelection from './DragSelection';
import AllViewEvent from './AllViewEvent';
import NonWorkingHoursOverlay from './NonWorkingHoursOverlay';
import { hoursForDate } from '../../utils/weekdayHours';
import EmployeeDetailsDialog from '../employees/EmployeeDetailsDialog';

// Week day abbreviations (Finnish)
const WEEKDAY_ABBR = ['Ma', 'Ti', 'Ke', 'To', 'Pe', 'La', 'Su'];

// calculateEventOverlaps is now imported from calendarConstants

function CalendarGrid({ t, isShortScreen = false }) {
  const canSchedule = useUserStore((s) => can(s.currentOrganization, 'work_orders.schedule'));
  const {
    currentView,
    employees,
    serviceBays,
    events,
    unavailabilities,
    calendarNotes,
    bookingEvents,
    breakPeriodEvents,
    getWeekDates,
    getEffectiveWorkHours,
    workSchedule,
    draggedMaintenance,
    setDraggedMaintenance,
    addEvent,
    removeFromUnscheduled,
    updateEmployee,
    filters,
    switchToDay,
  } = useCalendarState();

  // Employee details dialog state
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Track which cell is being hovered during sidebar drag
  const [dropHighlight, setDropHighlight] = useState(null);

  // Error state for drop operations
  const [dropError, setDropError] = useState(null);

  // Ref for the grid container (used by DragSelection for event handling)
  const gridRef = useRef(null);

  const weekDates = getWeekDates();

  // For 'all' view, use a single dummy resource to create one combined row
  const isAllView = currentView === 'all';

  // Apply resource filters
  const filteredEmployees = useMemo(() => {
    if (filters.employees.length === 0) return employees;
    return employees.filter((emp) => filters.employees.includes(emp.id));
  }, [employees, filters.employees]);

  const filteredServiceBays = useMemo(() => {
    if (filters.serviceBays.length === 0) return serviceBays;
    return serviceBays.filter((bay) => filters.serviceBays.includes(bay.id));
  }, [serviceBays, filters.serviceBays]);

  // Apply state filter to events
  const filteredEvents = useMemo(
    () => applyEventFilters(events, filters),
    [events, filters]
  );

  const resources = isAllView
    ? [{ id: 'all', name: t('All maintenances'), initials: 'ALL' }]
    : currentView === 'employee'
      ? filteredEmployees
      : filteredServiceBays;

  // Calculate dynamic hours based on work schedule (with filter overrides)
  const { startHour, endHour, hours, rowHeight } = useMemo(() => {
    const effectiveHours = getEffectiveWorkHours();
    // Parse effective hours (returns time strings like "08:00")
    const { startHour: start, endHour: end } = parseWorkScheduleHours(effectiveHours);

    // Use floor/ceil to handle partial hours (e.g., 23:55 should include the 23:00-24:00 slot)
    const startInt = Math.floor(start);
    const endInt = Math.ceil(end);

    // hours array is [8,9,10,11,12,13,14,15] for 8-16 work day (last slot is 15:00-16:00)
    const hoursArray = Array.from({ length: endInt - startInt }, (_, i) => startInt + i);
    return {
      startHour: startInt,
      endHour: endInt,
      hours: hoursArray,
      rowHeight: hoursArray.length * HOUR_HEIGHT,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.visibleStartHour, filters.visibleEndHour, getEffectiveWorkHours, workSchedule]);

  // Handle drag over for sidebar items
  const handleDragOver = useCallback((e, resourceId, dayIndex) => {
    if (!draggedMaintenance) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const hour = (y / HOUR_HEIGHT) + startHour;
    const snappedHour = Math.round(hour * SNAPS_PER_HOUR) / SNAPS_PER_HOUR;

    setDropHighlight({ resourceId, dayIndex, hour: snappedHour });
  }, [draggedMaintenance, startHour]);

  const handleDragLeave = useCallback(() => {
    setDropHighlight(null);
  }, []);

  // Handle drop from sidebar
  const handleDrop = useCallback(async (e, resourceId, dayIndex) => {
    e.preventDefault();
    setDropHighlight(null);
    if (!canSchedule) return;

    if (!draggedMaintenance) return;

    const droppedMaintenance = draggedMaintenance;
    setDraggedMaintenance(null);

    try {
      const rect = e.currentTarget.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const hour = (y / HOUR_HEIGHT) + startHour;
      const snappedHour = Math.round(hour * SNAPS_PER_HOUR) / SNAPS_PER_HOUR;

      const date = weekDates[dayIndex];
      const startTime = setHour(date, Math.floor(snappedHour), Math.round((snappedHour % 1) * 60));
      const endTime = setHour(date, Math.floor(snappedHour + 1), Math.round(((snappedHour + 1) % 1) * 60));

      const startTimeStr = format(startTime, 'yyyy-MM-ddTHH:mm:ss');
      const endTimeStr = format(endTime, 'yyyy-MM-ddTHH:mm:ss');

      const maintenanceId = droppedMaintenance.id;
      const scheduleData = {
        maintenance: maintenanceId,
        start_time: startTimeStr,
        end_time: endTimeStr,
      };

      if (currentView === 'bay') {
        scheduleData.service_bay = resourceId;
      } else if (currentView === 'employee') {
        // The row it was dropped on is this slot's team
        scheduleData.employee_ids = [resourceId];
      }

      const response = await axios.post('/api/scheduling/schedules/', scheduleData);
      const newSchedule = response.data;

      const eventEmployeeIds = newSchedule.employee_ids || droppedMaintenance.employee_ids || [];

      addEvent({
        id: newSchedule.id,
        maintenance_id: maintenanceId,
        start_time: startTimeStr,
        end_time: endTimeStr,
        vehicle_registration: droppedMaintenance.vehicle_registration,
        description: droppedMaintenance.description,
        customer_name: droppedMaintenance.customer_name,
        state: 'planned',
        service_bay_id: currentView === 'bay' ? resourceId : null,
        employee_ids: eventEmployeeIds,
      });

      removeFromUnscheduled(droppedMaintenance.id);
    } catch (err) {
      console.error('Failed to schedule maintenance:', err);
      const data = err.response?.data;
      const message = data?.detail || data?.message
        || (data?.maintenance && (Array.isArray(data.maintenance) ? data.maintenance[0] : data.maintenance))
        || t('Failed to schedule maintenance');
      setDropError(message);
    }
  }, [draggedMaintenance, weekDates, startHour, currentView, addEvent, removeFromUnscheduled, setDraggedMaintenance, t]);

  // Pre-compute events and unavailabilities lookup maps for O(1) access
  // Must be before early return to respect Rules of Hooks
  const { eventsMap, unavailabilitiesMap } = useMemo(() => {
    const evMap = {};
    const unavailMap = {};

    // Build events map: key = "resourceId-dayIndex"
    // Supports multi-day events by adding to each day's list with clamped hours
    filteredEvents.forEach((event) => {
      const resourceIds = currentView === 'employee'
        ? (event.employee_ids || [])
        : (event.service_bay_id ? [event.service_bay_id] : []);

      // Check if event spans multiple days
      const isMultiDay = event.local_start_date && event.local_end_date &&
        event.local_start_date !== event.local_end_date;

      if (isMultiDay) {
        // Multi-day event: add to each day between start and end
        weekDates.forEach((date, dayIndex) => {
          const dayDateStr = format(date, 'yyyy-MM-dd');

          // Check if this day is within the event's date range
          if (dayDateStr >= event.local_start_date && dayDateStr <= event.local_end_date) {
            const startsToday = event.local_start_date === dayDateStr;
            const endsToday = event.local_end_date === dayDateStr;

            // Create event instance with clamped hours for this day
            const clampedEvent = {
              ...event,
              _clampedStartHour: startsToday ? Math.max(startHour, event.local_start_hour) : startHour,
              _clampedEndHour: endsToday ? Math.min(endHour, event.local_end_hour) : endHour,
              _isMultiDay: true,
              _dayPosition: startsToday ? 'first' : (endsToday ? 'last' : 'middle'),
            };

            // Only add if valid (end > start)
            if (clampedEvent._clampedEndHour > clampedEvent._clampedStartHour) {
              resourceIds.forEach((resourceId) => {
                const key = `${resourceId}-${dayIndex}`;
                if (!evMap[key]) evMap[key] = [];
                evMap[key].push(clampedEvent);
              });
            }
          }
        });
      } else {
        // Single-day event: its column by date. The columns are the shown weekdays in order, so
        // with a closed Wednesday column 2 is Thursday; the weekday number would misplace it.
        const eventDateStr = event.local_start_date || format(parseISO(event.start_time), 'yyyy-MM-dd');
        const eventDayIndex = weekDates.findIndex((date) => format(date, 'yyyy-MM-dd') === eventDateStr);
        if (eventDayIndex === -1) return;

        resourceIds.forEach((resourceId) => {
          const key = `${resourceId}-${eventDayIndex}`;
          if (!evMap[key]) evMap[key] = [];
          evMap[key].push(event);
        });
      }
    });

    // Build unavailabilities map (for employee and all views)
    if (currentView === 'employee' || currentView === 'all') {
      weekDates.forEach((date, dayIndex) => {
        const dayDateStr = format(date, 'yyyy-MM-dd');

        unavailabilities.forEach((u) => {
          if (u.local_start_date <= dayDateStr && u.local_end_date >= dayDateStr) {
            const key = `${u.employee_id}-${dayIndex}`;
            if (!unavailMap[key]) unavailMap[key] = [];

            const startsToday = u.local_start_date === dayDateStr;
            const endsToday = u.local_end_date === dayDateStr;

            // Check if this is a multi-day unavailability
            const isMultiDay = u.local_start_date !== u.local_end_date;

            unavailMap[key].push({
              ...u,
              _clampedStartHour: startsToday ? Math.max(startHour, u.local_start_hour) : startHour,
              _clampedEndHour: endsToday ? Math.min(endHour, u.local_end_hour) : endHour,
              _isMultiDay: isMultiDay,
              _dayPosition: isMultiDay ? (startsToday ? 'first' : (endsToday ? 'last' : 'middle')) : undefined,
            });
          }
        });

        // Filter out invalid unavailabilities
        Object.keys(unavailMap).forEach((key) => {
          unavailMap[key] = unavailMap[key].filter((u) => u._clampedEndHour > u._clampedStartHour);
        });
      });
    }

    return { eventsMap: evMap, unavailabilitiesMap: unavailMap };
  }, [filteredEvents, unavailabilities, currentView, weekDates, startHour, endHour]);

  // Notes pinned to one mechanic's column. Notes without an employee belong to the whole
  // workshop and are drawn in the 'all' view instead, so they never enter this map.
  const notesMap = useMemo(() => {
    const map = {};
    if (currentView !== 'employee') return map;

    weekDates.forEach((date, dayIndex) => {
      const dayDateStr = format(date, 'yyyy-MM-dd');

      calendarNotes.forEach((note) => {
        if (!note.employee_id) return;
        if (note.local_start_date > dayDateStr || note.local_end_date < dayDateStr) return;

        const startsToday = note.local_start_date === dayDateStr;
        const endsToday = note.local_end_date === dayDateStr;
        const isMultiDay = note.local_start_date !== note.local_end_date;
        const clamped = {
          ...note,
          _clampedStartHour: startsToday ? Math.max(startHour, note.local_start_hour) : startHour,
          _clampedEndHour: endsToday ? Math.min(endHour, note.local_end_hour) : endHour,
          _isMultiDay: isMultiDay,
          _dayPosition: isMultiDay ? (startsToday ? 'first' : (endsToday ? 'last' : 'middle')) : undefined,
        };
        if (clamped._clampedEndHour <= clamped._clampedStartHour) return;

        const key = `${note.employee_id}-${dayIndex}`;
        if (!map[key]) map[key] = [];
        map[key].push(clamped);
      });
    });

    return map;
  }, [calendarNotes, currentView, weekDates, startHour, endHour]);

  // Build booking events map by day index
  const bookingEventsMap = useMemo(() => {
    const map = {};
    if (!bookingEvents || bookingEvents.length === 0) return map;

    weekDates.forEach((date, dayIndex) => {
      const dayDateStr = format(date, 'yyyy-MM-dd');
      const dayBookings = bookingEvents.filter((b) => b.local_start_date === dayDateStr);
      if (dayBookings.length > 0) {
        map[dayIndex] = dayBookings;
      }
    });
    return map;
  }, [bookingEvents, weekDates]);

  // Build break period events map by day index
  const breakPeriodEventsMap = useMemo(() => {
    const map = {};
    if (!breakPeriodEvents || breakPeriodEvents.length === 0) return map;

    weekDates.forEach((date, dayIndex) => {
      const dayDateStr = format(date, 'yyyy-MM-dd');
      const dayBreaks = breakPeriodEvents.filter((b) => b.local_start_date === dayDateStr);
      if (dayBreaks.length > 0) {
        map[dayIndex] = dayBreaks;
      }
    });
    return map;
  }, [breakPeriodEvents, weekDates]);

  // O(1) lookup functions
  const getEventsForResourceDay = (resourceId, dayIndex) => {
    return eventsMap[`${resourceId}-${dayIndex}`] || [];
  };

  const getUnavailabilitiesForResourceDay = (resourceId, dayIndex) => {
    return unavailabilitiesMap[`${resourceId}-${dayIndex}`] || [];
  };

  const getNotesForResourceDay = (resourceId, dayIndex) => {
    return notesMap[`${resourceId}-${dayIndex}`] || [];
  };

  // Handle drag end from DragSelection component
  // Must be before early return to respect Rules of Hooks
  const handleDragEnd = useCallback(async (selection) => {
    if (!canSchedule) return;
    const startDate = weekDates[selection.startDayIndex ?? selection.dayIndex];
    const endDate = selection.endDayIndex !== undefined
      ? weekDates[selection.endDayIndex]
      : startDate;

    // Scheduling mode: directly create schedule, no context menu
    const { schedulingModeTarget, exitSchedulingMode, clearDragSelection } = useCalendarState.getState();
    if (schedulingModeTarget) {
      try {
        const actualStart = Math.min(selection.startHour, selection.endHour);
        const actualEnd = Math.max(selection.startHour, selection.endHour);
        const startTime = setHour(startDate, Math.floor(actualStart), Math.round((actualStart % 1) * 60));
        const endTime = setHour(endDate, Math.floor(actualEnd), Math.round((actualEnd % 1) * 60));
        const startTimeStr = format(startTime, 'yyyy-MM-ddTHH:mm:ss');
        const endTimeStr = format(endTime, 'yyyy-MM-ddTHH:mm:ss');

        const scheduleData = {
          maintenance: schedulingModeTarget.id,
          start_time: startTimeStr,
          end_time: endTimeStr,
        };

        if (currentView === 'bay') {
          scheduleData.service_bay = selection.resourceId;
        } else if (currentView === 'employee') {
          // The row it was drawn in is this slot's team
          scheduleData.employee_ids = [selection.resourceId];
        }

        const response = await axios.post('/api/scheduling/schedules/', scheduleData);

        const eventEmployeeIds = response.data.employee_ids || schedulingModeTarget.employee_ids || [];

        addEvent({
          id: response.data.id,
          maintenance_id: schedulingModeTarget.id,
          start_time: startTimeStr,
          end_time: endTimeStr,
          vehicle_registration: schedulingModeTarget.vehicle_registration || '',
          description: schedulingModeTarget.description || '',
          customer_name: schedulingModeTarget.customer_name || null,
          state: 'planned',
          service_bay_id: currentView === 'bay' ? selection.resourceId : null,
          employee_ids: eventEmployeeIds,
        });

        removeFromUnscheduled(schedulingModeTarget.id);
        exitSchedulingMode();
        clearDragSelection();
      } catch (err) {
        const data = err.response?.data;
        const message = data?.detail || data?.message || t('Failed to schedule maintenance');
        setDropError(message);
        clearDragSelection();
      }
      return;
    }

    // Normal flow: open context menu
    useCalendarState.setState({
      contextMenuOpen: true,
      eventContextMenu: null,
      contextMenuPosition: selection.position,
      selectedTimeSlot: {
        resourceId: selection.resourceId,
        resourceType: currentView,
        dayIndex: selection.dayIndex,
        date: startDate,
        startHour: selection.startHour,
        endHour: selection.endHour,
        startDayIndex: selection.startDayIndex ?? selection.dayIndex,
        endDayIndex: selection.endDayIndex ?? selection.dayIndex,
        startDate,
        endDate,
        isMultiDay: selection.isMultiDay || false,
      },
    });
  }, [weekDates, currentView, addEvent, removeFromUnscheduled, t]);

  const handleDragCancel = useCallback(() => {
    useCalendarState.setState({
      contextMenuOpen: false,
      selectedTimeSlot: null,
    });
  }, []);

  // If no resources, show empty state (skip for 'all' view which always has content)
  if (!isAllView && resources.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 4,
        }}
      >
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          {currentView === 'employee' ? t('No employees yet') : t('No service bays yet')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {currentView === 'employee'
            ? t('Add employees to see them in the calendar')
            : t('Add service bays to see them in the calendar')}
        </Typography>
      </Box>
    );
  }

  // Get current time for indicator
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimePosition = (currentHour - startHour + currentMinute / 60) * HOUR_HEIGHT;

  return (
    <Box
      ref={gridRef}
      data-testid="calendar-grid"
      sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      {/* Drag Selection Overlay - uses direct DOM manipulation for performance */}
      <DragSelection
        gridRef={gridRef}
        workStartHour={startHour}
        workEndHour={endHour}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      />
      {/* Header Row */}
      <Box
        sx={{
          display: 'flex',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        {/* Time Header */}
        <Box
          sx={{
            width: TIME_COLUMN_WIDTH,
            minWidth: TIME_COLUMN_WIDTH,
            py: isShortScreen ? 0.5 : 1.5,
            px: 1.5,
            borderRight: 1,
            borderColor: 'divider',
            fontSize: '11px',
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            textAlign: 'center',
          }}
        >
          {t('Time')}
        </Box>

        {/* Resource Header */}
        <Box
          sx={{
            width: RESOURCE_COLUMN_WIDTH,
            minWidth: RESOURCE_COLUMN_WIDTH,
            py: isShortScreen ? 0.5 : 1.5,
            px: 1.5,
            borderRight: 1,
            borderColor: 'divider',
            fontSize: '11px',
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {isAllView
            ? t('Overview')
            : currentView === 'employee'
              ? t('Employee')
              : t('Service bay')}
        </Box>

        {/* Day Headers - clickable to switch to day view */}
        {weekDates.map((date, index) => {
          const isTodayCol = isToday(date);
          return (
            <Box
              key={index}
              onClick={() => switchToDay(date)}
              sx={{
                flex: 1,
                py: isShortScreen ? 0.5 : 1.5,
                px: 1.5,
                textAlign: 'center',
                borderRight: 1,
                borderColor: 'divider',
                bgcolor: isTodayCol ? UI_COLORS.todayBg : 'transparent',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: isTodayCol ? '#dbeafe' : 'action.hover',
                },
                transition: 'background-color 0.15s',
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                }}
              >
                {WEEKDAY_ABBR[date.getDay() === 0 ? 6 : date.getDay() - 1]}
              </Typography>
              <Typography
                sx={{
                  fontSize: isShortScreen ? '16px' : '24px',
                  fontWeight: 700,
                  mt: isShortScreen ? 0 : 0.5,
                  color: isTodayCol ? 'secondary.main' : 'text.primary',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                {date.getDate()}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Scrollable Body */}
      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex' }}>
        {/* Fixed Time Column */}
        <Box
          sx={{
            width: TIME_COLUMN_WIDTH,
            minWidth: TIME_COLUMN_WIDTH,
            flexShrink: 0,
            bgcolor: 'background.paper',
            borderRight: 1,
            borderColor: 'divider',
            position: 'sticky',
            left: 0,
            zIndex: 10,
          }}
        >
          {resources.map((resource) => (
            <Box
              key={resource.id}
              sx={{
                height: rowHeight,
                borderBottom: 2,
                borderColor: 'divider',
                position: 'relative',
              }}
            >
              {hours.map((hour) => (
                <Box
                  key={hour}
                  sx={{
                    height: HOUR_HEIGHT,
                    borderBottom: 1,
                    borderColor: UI_COLORS.hourLine,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    pt: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 500,
                      color: 'text.secondary',
                    }}
                  >
                    {hour}:00
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>

        {/* Resource Rows */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {resources.map((resource) => (
            <Box
              key={resource.id}
              data-resource-id={resource.id}
              sx={{
                display: 'flex',
                height: rowHeight,
                borderBottom: 2,
                borderColor: 'divider',
              }}
            >
              {/* Resource Info */}
              <Box
                onClick={currentView === 'employee' ? () => {
                  setSelectedEmployee(resource);
                  setEmployeeDialogOpen(true);
                } : undefined}
                sx={{
                  width: RESOURCE_COLUMN_WIDTH,
                  minWidth: RESOURCE_COLUMN_WIDTH,
                  p: 1.5,
                  borderRight: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.25,
                  position: 'sticky',
                  left: TIME_COLUMN_WIDTH,
                  zIndex: 5,
                  ...(currentView === 'employee' && {
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }),
                }}
              >
                {/* Avatar / Badge */}
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: currentView === 'employee' ? '50%' : '8px',
                    bgcolor: resource.color || 'secondary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {resource.initials || resource.id}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {resource.name}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                    {resource.title || (resource.role ? t(`role_${resource.role}`) : '') || resource.description || ''}
                  </Typography>
                </Box>
              </Box>

              {/* Day Columns */}
              {weekDates.map((date, dayIndex) => {
                const isTodayCol = isToday(date);
                const dayEvents = getEventsForResourceDay(resource.id, dayIndex);
                const dayUnavailabilities = getUnavailabilitiesForResourceDay(resource.id, dayIndex);
                const dayNotes = getNotesForResourceDay(resource.id, dayIndex);

                return (
                  <Box
                    key={dayIndex}
                    data-day-column={dayIndex}
                    data-day-date={format(date, 'yyyy-MM-dd')}
                    sx={{
                      flex: 1,
                      borderRight: 1,
                      borderColor: 'divider',
                      position: 'relative',
                      bgcolor: isTodayCol ? UI_COLORS.todayBg : 'transparent',
                      cursor: draggedMaintenance ? 'copy' : 'crosshair',
                      overflow: 'visible', // Allow events to visually move across columns
                    }}
                    onDragOver={(e) => handleDragOver(e, resource.id, dayIndex)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, resource.id, dayIndex)}
                  >
                    {/* Hour Grid Lines */}
                    {hours.map((hour) => (
                      <Box
                        key={hour}
                        sx={{
                          height: HOUR_HEIGHT,
                          borderBottom: 1,
                          borderColor: UI_COLORS.hourLine,
                          position: 'relative',
                        }}
                      >
                        {/* Half-hour line */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: 0,
                            right: 0,
                            height: '1px',
                            bgcolor: UI_COLORS.halfHourLine,
                          }}
                        />
                      </Box>
                    ))}

                    {/* Outside this day's work hours: the employee's own shift in the employee
                        view, the workshop's hours of that weekday in the bay view */}
                    <NonWorkingHoursOverlay
                      hours={currentView === 'employee'
                        ? (resource.work_hours ? hoursForDate(resource.work_hours, date) : undefined)
                        : hoursForDate(workSchedule.days, date)}
                      startHour={startHour}
                      endHour={endHour}
                      hourHeight={HOUR_HEIGHT}
                    />

                    {/* Current Time Line (only for today) */}
                    {isTodayCol && currentTimePosition > 0 && currentTimePosition < rowHeight && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: currentTimePosition,
                          left: 0,
                          right: 0,
                          zIndex: 5,
                          // Drawn above the cards but never takes the click (short cards are mostly line)
                          pointerEvents: 'none',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: UI_COLORS.currentTimeLine,
                            ml: '-4px',
                          }}
                        />
                        <Box sx={{ flex: 1, height: 2, bgcolor: UI_COLORS.currentTimeLine }} />
                      </Box>
                    )}

                    {/* Drop Highlight for sidebar drag */}
                    {dropHighlight &&
                      dropHighlight.resourceId === resource.id &&
                      dropHighlight.dayIndex === dayIndex && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 4,
                            right: 4,
                            top: (dropHighlight.hour - startHour) * HOUR_HEIGHT + 2,
                            height: HOUR_HEIGHT - 4,
                            bgcolor: 'rgba(59, 130, 246, 0.2)',
                            border: '2px dashed #3b82f6',
                            borderRadius: '8px',
                            pointerEvents: 'none',
                            zIndex: 50,
                          }}
                        />
                      )}

                    {/* Cross-day resize preview is now handled by DraggableEvent's fixed overlay */}

                    {/* Events - use AllViewEvent with overlap calculation for 'all' view */}
                    {isAllView ? (
                      // For 'all' view, get ALL filtered events and unavailabilities for this day
                      (() => {
                        const dayDateStr = format(date, 'yyyy-MM-dd');

                        // Get maintenance events for this day
                        const allDayEvents = filteredEvents.filter((event) => {
                          // Check if event spans this day (support multi-day events)
                          if (event.local_start_date && event.local_end_date) {
                            return dayDateStr >= event.local_start_date && dayDateStr <= event.local_end_date;
                          }
                          // Fallback for events without local_* fields
                          return format(parseISO(event.start_time), 'yyyy-MM-dd') === dayDateStr;
                        }).map((event) => {
                          // For multi-day events, clamp hours for this specific day
                          if (event.local_start_date && event.local_end_date &&
                              event.local_start_date !== event.local_end_date) {
                            const startsToday = event.local_start_date === dayDateStr;
                            const endsToday = event.local_end_date === dayDateStr;
                            return {
                              ...event,
                              _itemType: 'maintenance',
                              _clampedStartHour: startsToday ? event.local_start_hour : startHour,
                              _clampedEndHour: endsToday ? event.local_end_hour : endHour,
                              _isMultiDay: true,
                              _dayPosition: startsToday ? 'first' : (endsToday ? 'last' : 'middle'),
                            };
                          }
                          return { ...event, _itemType: 'maintenance' };
                        });

                        // Get unavailabilities for this day (all employees)
                        const allDayUnavailabilities = unavailabilities.filter((u) => {
                          return u.local_start_date <= dayDateStr && u.local_end_date >= dayDateStr;
                        }).map((u) => {
                          const startsToday = u.local_start_date === dayDateStr;
                          const endsToday = u.local_end_date === dayDateStr;
                          const isMultiDay = u.local_start_date !== u.local_end_date;
                          return {
                            ...u,
                            _itemType: 'unavailability',
                            _clampedStartHour: startsToday ? Math.max(startHour, u.local_start_hour) : startHour,
                            _clampedEndHour: endsToday ? Math.min(endHour, u.local_end_hour) : endHour,
                            _isMultiDay: isMultiDay,
                            _dayPosition: isMultiDay ? (startsToday ? 'first' : (endsToday ? 'last' : 'middle')) : undefined,
                          };
                        }).filter((u) => u._clampedEndHour > u._clampedStartHour);

                        // Get calendar notes for this day
                        const allDayNotes = calendarNotes.filter((n) => {
                          return n.local_start_date <= dayDateStr && n.local_end_date >= dayDateStr;
                        }).map((n) => {
                          const startsToday = n.local_start_date === dayDateStr;
                          const endsToday = n.local_end_date === dayDateStr;
                          const isMultiDay = n.local_start_date !== n.local_end_date;
                          return {
                            ...n,
                            _itemType: 'calendar_note',
                            _clampedStartHour: startsToday ? Math.max(startHour, n.local_start_hour) : startHour,
                            _clampedEndHour: endsToday ? Math.min(endHour, n.local_end_hour) : endHour,
                            _isMultiDay: isMultiDay,
                            _dayPosition: isMultiDay ? (startsToday ? 'first' : (endsToday ? 'last' : 'middle')) : undefined,
                          };
                        }).filter((n) => n._clampedEndHour > n._clampedStartHour);

                        // Combine events, unavailabilities, and notes
                        const allItems = [...allDayEvents, ...allDayUnavailabilities, ...allDayNotes];
                        const itemsWithOverlaps = calculateEventOverlaps(allItems, startHour, parseISO);

                        return itemsWithOverlaps.map((item) => {
                          if (item._itemType === 'unavailability') {
                            return (
                              <UnavailabilityEvent
                                key={`unavail-${item.id}-${dayIndex}`}
                                unavailability={item}
                                startHour={startHour}
                                endHour={endHour}
                                dayIndex={dayIndex}
                                overlapIndex={item.overlapIndex}
                                overlapTotal={item.overlapTotal}
                                employees={employees}
                                showEmployeeName
                              />
                            );
                          }
                          if (item._itemType === 'calendar_note') {
                            return (
                              <CalendarNoteEvent
                                key={`note-${item.id}-${dayIndex}`}
                                note={item}
                                startHour={startHour}
                                endHour={endHour}
                                dayIndex={dayIndex}
                                overlapIndex={item.overlapIndex}
                                overlapTotal={item.overlapTotal}
                              />
                            );
                          }
                          return (
                            <AllViewEvent
                              key={item._isMultiDay ? `${item.id}-${dayIndex}` : item.id}
                              event={item}
                              startHour={startHour}
                              endHour={endHour}
                              dayIndex={dayIndex}
                              overlapIndex={item.overlapIndex}
                              overlapTotal={item.overlapTotal}
                              employees={employees}
                              serviceBays={serviceBays}
                            />
                          );
                        });
                      })()
                    ) : (
                      // For employee/bay views, calculate overlaps for side-by-side display
                      // Combine maintenances and unavailabilities so they stack together
                      (() => {
                        // Mark items with their type and combine
                        const maintenancesWithType = dayEvents.map((e) => ({ ...e, _itemType: 'maintenance' }));
                        const unavailabilitiesWithType = dayUnavailabilities.map((u) => ({ ...u, _itemType: 'unavailability' }));
                        const notesWithType = dayNotes.map((n) => ({ ...n, _itemType: 'calendar_note' }));
                        const allItems = [...maintenancesWithType, ...unavailabilitiesWithType, ...notesWithType];

                        const itemsWithOverlaps = calculateEventOverlaps(allItems, startHour, parseISO);
                        return itemsWithOverlaps.map((item) => {
                          if (item._itemType === 'calendar_note') {
                            return (
                              <CalendarNoteEvent
                                key={`note-${item.id}-${dayIndex}`}
                                note={item}
                                startHour={startHour}
                                endHour={endHour}
                                dayIndex={dayIndex}
                                overlapIndex={item.overlapIndex}
                                overlapTotal={item.overlapTotal}
                              />
                            );
                          }
                          if (item._itemType === 'unavailability') {
                            return (
                              <UnavailabilityEvent
                                key={`unavail-${item.id}-${dayIndex}`}
                                unavailability={item}
                                startHour={startHour}
                                endHour={endHour}
                                dayIndex={dayIndex}
                                overlapIndex={item.overlapIndex}
                                overlapTotal={item.overlapTotal}
                              />
                            );
                          }
                          return (
                            <MaintenanceEvent
                              key={item._isMultiDay ? `${item.id}-${dayIndex}` : item.id}
                              event={item}
                              startHour={startHour}
                              endHour={endHour}
                              dayIndex={dayIndex}
                              overlapIndex={item.overlapIndex}
                              overlapTotal={item.overlapTotal}
                            />
                          );
                        });
                      })()
                    )}

                    {/* Break period overlays */}
                    {(breakPeriodEventsMap[dayIndex] || []).map((breakPeriod) => {
                      const topHour = breakPeriod.local_start_hour - startHour;
                      const durationHours = breakPeriod.local_end_hour - breakPeriod.local_start_hour;
                      if (topHour < 0 || durationHours <= 0) return null;

                      return (
                        <Box
                          key={breakPeriod.id}
                          sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: topHour * HOUR_HEIGHT,
                            height: durationHours * HOUR_HEIGHT,
                            bgcolor: BREAK_PERIOD_CONFIG.bg,
                            borderTop: `1px dashed ${BREAK_PERIOD_CONFIG.color}`,
                            borderBottom: `1px dashed ${BREAK_PERIOD_CONFIG.color}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            zIndex: 1,
                            '&:hover': {
                              bgcolor: 'rgba(148, 163, 184, 0.2)',
                            },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = '/settings?tab=booking';
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '9px',
                              fontWeight: 500,
                              color: BREAK_PERIOD_CONFIG.text,
                              opacity: 0.7,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              userSelect: 'none',
                            }}
                          >
                            {breakPeriod.name || t('booking.calendar.break_period')}
                          </Typography>
                        </Box>
                      );
                    })}

                    {/* Booking events overlay */}
                    {(bookingEventsMap[dayIndex] || []).map((booking) => {
                      const bookingConfig = BOOKING_STATUS_CONFIG[booking.status] || BOOKING_STATUS_CONFIG.pending;
                      const topHour = booking.local_start_hour - startHour;
                      const durationHours = booking.local_end_hour - booking.local_start_hour;
                      if (topHour < 0 || durationHours <= 0) return null;

                      return (
                        <Box
                          key={booking.id}
                          sx={{
                            position: 'absolute',
                            left: 4,
                            right: 4,
                            top: topHour * HOUR_HEIGHT + 2,
                            height: durationHours * HOUR_HEIGHT - 4,
                            bgcolor: bookingConfig.bg,
                            border: `2px ${bookingConfig.borderStyle} ${bookingConfig.color}`,
                            borderRadius: '6px',
                            px: 0.75,
                            py: 0.25,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            zIndex: 3,
                            opacity: 0.9,
                            '&:hover': { opacity: 1, zIndex: 10 },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = '/bookings';
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '10px',
                              fontWeight: 600,
                              color: bookingConfig.text,
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {booking.service_name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '9px',
                              color: bookingConfig.text,
                              opacity: 0.8,
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {booking.customer_name} {booking.vehicle_registration && `\u00B7 ${booking.vehicle_registration}`}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '8px',
                              color: bookingConfig.color,
                              fontWeight: 600,
                              mt: 0.25,
                            }}
                          >
                            {t('booking.calendar.online_booking')}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Error Snackbar for drop operations */}
      <Snackbar
        open={!!dropError}
        autoHideDuration={5000}
        onClose={() => setDropError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setDropError(null)} severity="error" sx={{ width: '100%' }}>
          {dropError}
        </Alert>
      </Snackbar>

      {/* Employee Details Dialog */}
      {selectedEmployee && (
        <EmployeeDetailsDialog
          open={employeeDialogOpen}
          onClose={() => {
            setEmployeeDialogOpen(false);
            // Re-fetch employee data to pick up any work hour changes
            axios.get(`/api/employees/${selectedEmployee.id}/`).then((res) => {
              const emp = res.data;
              updateEmployee(selectedEmployee.id, {
                work_day_start: emp.effective_work_day_start || null,
                work_day_end: emp.effective_work_day_end || null,
                working_weekdays: emp.effective_working_weekdays,
                work_hours: emp.effective_work_hours,
                name: emp.name,
                title: emp.title,
              });
            }).catch(() => {});
            setSelectedEmployee(null);
          }}
          employee={selectedEmployee}
        />
      )}
    </Box>
  );
}

export default withTranslation()(CalendarGrid);
